/**
 * Public social-proof booking stats for the homepage trust line.
 * Mirrors get_public_booking_stats() RPC: count by rental_requests.created_at,
 * exclude denied/cancelled/archived.
 */

export const HERO_TRUST_LINE_DEFAULT =
  'Vi hjälper fler än 100 kunder per år – företag, organisationer och privatpersoner.';

/** Statuses that are not real customer bookings (audited from rental_request_status + customer stats). */
export const PUBLIC_BOOKING_STATS_EXCLUDED_STATUSES = ['denied', 'cancelled'] as const;

export type PublicBookingStatsPayload = {
  current_year: number;
  current_year_count: number;
  previous_year: number;
  previous_year_count: number;
};

export type SocialProofBookingMode = 'previous_year' | 'current_ytd';

export type SocialProofBookingDisplay = {
  mode: SocialProofBookingMode;
  year: number;
  count: number;
  line: string;
};

export type PublicBookingStatRow = {
  created_at: string;
  status: string;
  archived?: boolean | null;
  /** Event/start date — intentionally ignored for year assignment */
  event_date?: string | null;
  start_date?: string | null;
};

const STOCKHOLM = 'Europe/Stockholm';

/** Calendar parts in Europe/Stockholm (year, month 1–12, day). */
export function getStockholmDateParts(now: Date = new Date()): {
  year: number;
  month: number;
  day: number;
} {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: STOCKHOLM,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  return { year, month, day };
}

/**
 * 1 Jan – 31 Mar → previous calendar year total
 * 1 Apr – 31 Dec → current calendar year YTD
 */
export function resolveSocialProofBookingMode(now: Date = new Date()): SocialProofBookingMode {
  const { month } = getStockholmDateParts(now);
  return month <= 3 ? 'previous_year' : 'current_ytd';
}

/** Same inclusion rules as the SQL RPC (status + archived). Year comes from created_at. */
export function countsTowardPublicBookingStats(row: PublicBookingStatRow): boolean {
  if (row.archived === true) return false;
  const status = String(row.status || '').trim().toLowerCase();
  if ((PUBLIC_BOOKING_STATS_EXCLUDED_STATUSES as readonly string[]).includes(status)) {
    return false;
  }
  return true;
}

export function bookingCreatedYearStockholm(createdAt: string): number | null {
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  return getStockholmDateParts(d).year;
}

/** Unit-testable aggregate mirroring the RPC year buckets. */
export function countPublicBookingsForYear(
  rows: PublicBookingStatRow[],
  year: number,
): number {
  return rows.reduce((acc, row) => {
    if (!countsTowardPublicBookingStats(row)) return acc;
    const createdYear = bookingCreatedYearStockholm(row.created_at);
    if (createdYear !== year) return acc;
    return acc + 1;
  }, 0);
}

export function formatSocialProofBookingLine(
  mode: SocialProofBookingMode,
  year: number,
  count: number,
): string {
  if (mode === 'previous_year') {
    return `Under ${year} fick vi ${count} bokningar.`;
  }
  return `Hittills i år har vi fått ${count} bokningar.`;
}

export const SOCIAL_PROOF_CTA_LABEL = 'Boka din fest →';

/**
 * Build display from RPC payload. Returns null when count is missing/invalid
 * or zero (never invent a "0 bokningar" social-proof line from bad/empty data).
 */
export function resolveSocialProofBookingDisplay(
  stats: PublicBookingStatsPayload | null | undefined,
  now: Date = new Date(),
): SocialProofBookingDisplay | null {
  if (!stats) return null;

  const mode = resolveSocialProofBookingMode(now);
  const year = mode === 'previous_year' ? stats.previous_year : stats.current_year;
  const rawCount =
    mode === 'previous_year' ? stats.previous_year_count : stats.current_year_count;

  if (typeof year !== 'number' || !Number.isFinite(year)) return null;
  if (typeof rawCount !== 'number' || !Number.isFinite(rawCount)) return null;
  const count = Math.floor(rawCount);
  if (count <= 0) return null;

  return {
    mode,
    year,
    count,
    line: formatSocialProofBookingLine(mode, year, count),
  };
}

/** Normalize RPC JSON; null on fetch/parse failure (caller must not show 0). */
export function normalizePublicBookingStatsPayload(
  data: unknown,
): PublicBookingStatsPayload | null {
  if (!data || typeof data !== 'object') return null;
  const row = data as Record<string, unknown>;
  const current_year = Number(row.current_year);
  const previous_year = Number(row.previous_year);
  const current_year_count = Number(row.current_year_count);
  const previous_year_count = Number(row.previous_year_count);

  if (
    ![current_year, previous_year, current_year_count, previous_year_count].every(
      (n) => Number.isFinite(n),
    )
  ) {
    return null;
  }

  return {
    current_year,
    previous_year,
    current_year_count,
    previous_year_count,
  };
}
